import React from "react";
import { useNavigate } from "react-router-dom";
import { company } from "../models/dataModel";
import { navLinkProps } from "../controllers/appController";
import Logo from "./Logo";
import useServiceContext from "../hooks/useServiceContext";
import Breadcrumbs from "./Breadcrumbs";
import { paths } from "../models/paths";
import "./Footer.css";

// Footer columns (content pack section 8, S17). The "Areas" column is left
// out while the suburb pages are on hold (C14).
const serviceLinks = [
  { label: "Regular house cleaning", path: paths.regular },
  { label: "Deep cleaning", path: paths.deep },
  { label: "End of tenancy cleaning", path: paths.endOfTenancy },
  { label: "Oven and carpet cleaning", path: paths.ovenCarpet },
  { label: "Office cleaning", path: paths.office },
  { label: "School cleaning", path: paths.school },
];

const companyLinks = [
  { label: "House cleaning", path: paths.houseHub },
  { label: "House cleaning prices", path: paths.prices },
  { label: "Commercial cleaning", path: paths.commercial },
  { label: "About", path: paths.about },
  { label: "Projects", path: paths.projects },
  { label: "Blog", path: paths.blog },
  { label: "Contact", path: paths.contact },
];

const Footer = () => {
  const navigate = useNavigate();
  const year = new Date().getFullYear();

  const isDomestic = useServiceContext() === "domestic";

  return (
    <>
    <Breadcrumbs />
    <footer className="footer">
      <div className="footer__top">
        <div className="container footer__grid">

          {/* ── Brand ── */}
          <div className="footer__brand">
            <div className="footer__logo">
              <Logo light={true} height={44} />
            </div>
            <p className="footer__brand-desc">
              {isDomestic
                ? "Premium residential cleaning services for homes and apartments in Auckland. Standard Clean and All-Inclusive Deep Clean starting with a free inspection."
                : "Delivering world-class cleaning, landscaping, and pest control services to institutional and commercial facilities across New Zealand."}
            </p>
            {/* Only provable claims here; licence/compliance badges wait on S9 evidence. */}
            <div className="footer__badges">
              <span className="footer__badge">✓ Free Inspection</span>
              <span className="footer__badge">✓ Fixed Written Quotes</span>
              <span className="footer__badge">✓ Since {company.founded}</span>
            </div>
          </div>

          {/* ── Services ── */}
          <div className="footer__col">
            <span className="footer__col-title">Services</span>
            <ul className="footer__col-links">
              {serviceLinks.map((link) => (
                <li key={link.path}>
                  <a {...navLinkProps(navigate, link.path, null)}>{link.label}</a>
                </li>
              ))}
            </ul>
          </div>

          {/* ── Company ── */}
          <div className="footer__col">
            <span className="footer__col-title">Company</span>
            <ul className="footer__col-links">
              {companyLinks.map((link) => (
                <li key={link.path}>
                  <a {...navLinkProps(navigate, link.path, null)}>{link.label}</a>
                </li>
              ))}
            </ul>
          </div>

          {/* ── Contact ── */}
          <div className="footer__col">
            <span className="footer__col-title">Contact</span>
            {/* Name, address and phone must match the Google Business Profile (S2). */}
            <ul className="footer__col-info">
              <li>
                <span className="footer__info-icon">🏢</span>
                <span>{company.name}</span>
              </li>
              <li>
                <span className="footer__info-icon">📍</span>
                <span>{company.address}</span>
              </li>
              <li>
                <span className="footer__info-icon">📞</span>
                <a href={`tel:${company.phone}`}>{company.phone}</a>
              </li>
              <li>
                <span className="footer__info-icon">✉️</span>
                <a href={`mailto:${company.email}`}>{company.email}</a>
              </li>
              <li>
                <span className="footer__info-icon">🌐</span>
                <a href={`https://${company.website}`} target="_blank" rel="noopener noreferrer">{company.website}</a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="footer__divider" />

      <div className="footer__bottom">
        <div className="container footer__bottom-inner">
          <span>© {year} {company.name}. All rights reserved.</span>
          <span>Auckland, New Zealand</span>
        </div>
      </div>
    </footer>
    </>
  );
};

export default Footer;
